/**
 * Sidebar sections for the command-center shell (the WorkQueue rail).
 *
 * Kept out of the component file so the feature-flag filter is a pure
 * function tests can call with either flag state.
 */
import {
  Layers,
  AlertCircle,
  Activity,
  Calendar,
  Settings,
  Grid3X3,
  Inbox,
  ClipboardList,
  Landmark,
  Megaphone,
  Shield,
} from 'lucide-react';
import { NAV_FEATURE_FLAGS, type NavFeature, type NavFeatureFlags } from '@/lib/navAccess';

export interface WorkQueueNavItem {
  path: string;
  label: string;
  icon: typeof Layers;
  description: string;
  aliases?: string[];
  /** When set, the entry is dropped entirely while that feature is off. */
  feature?: NavFeature;
  /** When set, the entry stays but takes this label while that feature is off. */
  whileDormant?: { feature: NavFeature; label: string; description: string };
}

export interface WorkQueueNavSection {
  title: string;
  items: WorkQueueNavItem[];
}

// Navigation items that load separate pages
export const ALL_NAV_SECTIONS: WorkQueueNavSection[] = [
  {
    title: 'Operate',
    items: [
      { path: '/todays-calls', label: 'Today', icon: Inbox, description: 'Daily calls' },
      { path: '/command', label: 'Rates', icon: Grid3X3, description: 'Rate grid' },
      {
        path: '/workspace/queue',
        label: 'Work Queue',
        icon: ClipboardList,
        description: 'Approvals',
        aliases: [
          '/marketing-approvals',
          '/engagement-review',
          '/approvals',
          '/content-review',
          '/organic-content',
          '/publishing-ops',
          '/organic-packages',
        ],
      },
    ],
  },
  {
    title: 'Signal',
    items: [
      { path: '/events', label: 'Calendar', icon: Calendar, description: 'Events' },
      {
        path: '/intelligence',
        label: 'Intelligence',
        icon: Activity,
        description: 'Market view',
        aliases: ['/kpis', '/insights', '/forecasting', '/acquisitions', '/labor', '/results'],
      },
      {
        path: '/marketing-command-center',
        label: 'Marketing',
        icon: Megaphone,
        description: 'Paid media',
        aliases: ['/marketing-analytics'],
        // Only the Web Analytics tab is live while marketing is dormant.
        whileDormant: { feature: 'marketing', label: 'Web Analytics', description: 'Site traffic' },
      },
      {
        path: '/brand-defence',
        label: 'Brand Defence',
        icon: Shield,
        description: 'Protect branded search',
        feature: 'marketing',
      },
      {
        path: '/finance',
        label: 'Finance',
        icon: Landmark,
        description: 'Deals',
        aliases: ['/finance/deals'],
      },
    ],
  },
  {
    title: 'Admin',
    items: [
      {
        path: '/system',
        label: 'System',
        icon: AlertCircle,
        description: 'Health',
        aliases: ['/control-tower', '/system-map', '/learning', '/oracle-performance'],
      },
      {
        path: '/settings',
        label: 'Settings',
        icon: Settings,
        description: 'Config',
        aliases: ['/admin/settings', '/settings/pricing-boundaries'],
      },
    ],
  },
];

function featureEnabled(feature: NavFeature | undefined, flags: NavFeatureFlags): boolean {
  if (!feature) return true;
  switch (feature) {
    case 'marketing':
      return flags.marketingUiEnabled;
  }
}

/** Sidebar sections with feature flags applied. Sections left empty are dropped. */
export function navSectionsForFlags(flags: NavFeatureFlags): WorkQueueNavSection[] {
  return ALL_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => featureEnabled(item.feature, flags))
      .map((item) =>
        item.whileDormant && !featureEnabled(item.whileDormant.feature, flags)
          ? {
              ...item,
              label: item.whileDormant.label,
              description: item.whileDormant.description,
            }
          : item,
      ),
  })).filter((section) => section.items.length > 0);
}

export const NAV_SECTIONS: WorkQueueNavSection[] = navSectionsForFlags(NAV_FEATURE_FLAGS);
